import React, { ReactElement, useState } from 'react';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import Modal, { ModalProps } from 'react-bootstrap/Modal';
import { StrapiApiError } from 'v2/models';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import styles from './confirmationModal.module.scss';
import DraggableModalDialog from 'v2/common/DraggableModalDialog/DraggableModalDialog';
import dragIcon from 'v2/images/icon-drag.svg';

export interface ConfirmationModalProps extends ModalProps {
  show: boolean;
  onCancel: (source?: 'cancel' | 'close' | 'error') => void;
  onOk: (() => Promise<unknown>) | (() => void);
  title: string | ReactElement;
  cancelBtnText?: string;
  okBtnText?: string;
  okBtnDisabled?: boolean;
  cancelBtnDisabled?: boolean;
  isLoading?: boolean;
  className?: string;
  isDraggable?: boolean;
  classes?: {
    header?: string;
    body?: string;
    backdrop?: string;
  };
  children: ReactElement;
}

const ConfirmationModal = ({
  show,
  onCancel,
  onOk,
  title,
  cancelBtnText = 'Cancel',
  okBtnText = 'Ok',
  okBtnDisabled = false,
  cancelBtnDisabled = false,
  isLoading = false,
  className,
  classes,
  children,
  isDraggable = false,
  ...modalProps
}: ConfirmationModalProps) => {
  const [isPromiseLoading, setIsPromiseLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleClick = async () => {
    try {
      setIsPromiseLoading(true);
      await onOk();
    } catch (error) {
      onCancel('error');
      toast.error((error as StrapiApiError).message);
    }
    setIsPromiseLoading(false);
  };

  const headerClasses = [
    'align-items-center',
    classes?.header,
    {
      [styles.draggableHeader]: isDraggable,
    },
  ];

  return (
    <Modal
      data-cy="modal"
      show={show}
      backdropClassName={classnames({
        [styles.backDrop]: isDraggable,
      })}
      onHide={() => {
        setIsDragging(false);
        onCancel('close');
      }}
      className={classnames(className, styles.customModal, {
        draggableModal: isDraggable,
        [styles.modalScroll]: isDraggable,
      })}
      dialogAs={isDraggable ? DraggableModalDialog : undefined}
      scrollable={isDraggable}
      {...modalProps}
    >
      <Modal.Header
        className={classnames(headerClasses)}
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
        data-cy="confirmation-modal:header-modal"
      >
        <Modal.Title data-cy="modal-title">{title}</Modal.Title>
        {isDraggable && (
          <img
            src={dragIcon}
            alt="drag-icon"
            className={classnames([
              styles.dragIcon,
              {
                visible: isDragging,
              },
            ])}
            draggable="false"
          />
        )}
      </Modal.Header>
      <Modal.Body className={classes?.body}>{children}</Modal.Body>
      <Modal.Footer>
        <button
          className={classnames('btn btn-primary-outline btn-secondary')}
          onClick={() => onCancel('cancel')}
          aria-label="cancel-modal"
          data-cy={'confirmation-modal:confirmation-modal-cancel-button'}
          disabled={cancelBtnDisabled}
        >
          {cancelBtnText}
        </button>
        <button
          className="btn btn-primary"
          disabled={isPromiseLoading || isLoading || okBtnDisabled}
          onClick={handleClick}
          aria-label={okBtnText}
          data-cy={'confirmation-modal:confirmation-modal-ok-button'}
        >
          {isPromiseLoading || isLoading ? (
            <LoadingIndicator className={styles.loadingIndicator} />
          ) : (
            okBtnText
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationModal;
